import { PageList } from '@shared/types/filter';

// ---- Манифесты (нужны только для поиска в модалке создания приёмки) ----

export enum ManifestStatus {
  OnTheWay = 'OnTheWay',
  Arrival = 'Arrival',
  StartAcceptance = 'StartAcceptance',
  Closed = 'Closed',
}

export interface ManifestDto {
  acceptanceActId?: number;
  /** Номер приёмки ACT-XXXXXXXX; пуст, пока по манифесту не открыта приёмка */
  acceptanceNumber?: string | null;
  number: string;
  manifestStatus: ManifestStatus | number | string;
  bagsCount: number;
}

export type GetAllManifestsHttpResponse = PageList<ManifestDto>;

const manifestStatusByCode: Record<number, ManifestStatus> = {
  0: ManifestStatus.OnTheWay,
  1: ManifestStatus.Arrival,
  2: ManifestStatus.StartAcceptance,
  3: ManifestStatus.Closed,
};

/** Gateway отдаёт статус строкой, но на всякий случай понимаем и числовой код */
export const normalizeManifestStatus = (status: unknown): ManifestStatus | undefined => {
  if (typeof status === 'number') return manifestStatusByCode[status];
  if (typeof status === 'string') {
    if (Object.values(ManifestStatus).includes(status as ManifestStatus)) return status as ManifestStatus;
    const parsed = Number(status);
    if (!Number.isNaN(parsed)) return manifestStatusByCode[parsed];
  }
  return undefined;
};

export interface ManifestOperationHttpResponse {
  status: ManifestOperationResultStatus;
}

export enum ManifestOperationResultStatus {
  Success,
  NotFound,
  InvalidStatus,
  AcceptanceActEmpty,
  NotFoundDropOffPoint,
  HasErrorApiIntegration,
}

export interface NotAcceptedManifestBagsHttpResponse {
  manifestNumber: string;
  totalCount: number;
  acceptedCount: number;
  notAcceptedCount: number;
  bags: NotAcceptedManifestBagDto[];
}

export interface NotAcceptedManifestBagDto {
  number: string;
}

// ---- Мешки в приёмке ----

export interface ManifestBagDto {
  number: string;
  acceptedBy?: {
    login: string;
  };
  acceptanceDate?: string;
  /** Мешка не было ни в одном манифесте — принят как незаявленный */
  isUndeclared?: boolean;
}

export interface AcceptanceBagHttpRequest {
  acceptanceActId: number;
}

export interface AcceptanceBagHttpResponse {
  status: AcceptanceBagStatus;
}

export enum AcceptanceBagStatus {
  Already,
  Accepted,
  NotFound,
  UserNotFound,
  AcceptanceActNotFound,
  AcceptanceActAlreadyClosed,
  /** Принят, но ни в одном манифесте его нет — оператору нужен алерт */
  AcceptedUndeclared,
  /** Скан не похож на номер мешка (маска в settings) — не принят */
  InvalidBagNumber,
  /** Отсканирован номер манифеста вместо мешка — не принят */
  ManifestNumberScanned,
}

// ---- Приёмки ----

export enum AcceptanceActStatus {
  Created = 'Created',
  Closed = 'Closed',
}

export interface AcceptanceActDto {
  id: number;
  /** Номер приёмки ACT-XXXXXXXX */
  number: string;
  vehicleNumber: string;
  /** null — приёмка без манифеста */
  manifestNumber?: string | null;
  status: AcceptanceActStatus | number | string;
  createdAt: string;
  closeAt?: string | null;
  bagsCount: number;
  undeclaredBagsCount: number;
  openedByLogin?: string | null;
}

export type GetAllAcceptanceActsHttpResponse = PageList<AcceptanceActDto>;

export enum CreateAcceptanceActStatus {
  Success,
  ManifestNotFound,
  ManifestAlreadyAccepting,
  ManifestInvalidStatus,
  NotFoundDropOffPoint,
  HasErrorApiIntegration,
}

export interface CreateAcceptanceActHttpRequest {
  vehicleNumber: string;
  /** Пусто — приёмка без манифеста */
  manifestNumber?: string | null;
}

export interface CreateAcceptanceActHttpResponse {
  status: CreateAcceptanceActStatus;
  /** Заполнены, когда приёмка создана (Success и HasErrorApiIntegration) */
  id?: number | null;
  number?: string | null;
  manifestNumber?: string | null;
  /** Номер приёмки, в которой манифест уже принимается (ManifestAlreadyAccepting) */
  existingAcceptanceNumber?: string | null;
}

export interface AcceptanceActBagsHttpResponse {
  id: number;
  number: string;
  vehicleNumber: string;
  manifestNumber?: string | null;
  status: AcceptanceActStatus | number | string;
  createdAt: string;
  closeAt?: string | null;
  openedByLogin?: string | null;
  bags: ManifestBagDto[];
}

/** Приёмка, открытая на экране сканирования: по манифесту (manifestNumber задан) или без него */
export interface CurrentAcceptance {
  acceptanceActId: number;
  acceptanceNumber?: string | null;
  manifestNumber?: string | null;
}

const acceptanceActStatusByCode: Record<number, AcceptanceActStatus> = {
  0: AcceptanceActStatus.Created,
  1: AcceptanceActStatus.Closed,
};

/** Gateway отдаёт статус строкой, но на всякий случай понимаем и числовой код */
export const normalizeAcceptanceActStatus = (status: unknown): AcceptanceActStatus | undefined => {
  if (typeof status === 'number') return acceptanceActStatusByCode[status];
  if (typeof status === 'string') {
    if (Object.values(AcceptanceActStatus).includes(status as AcceptanceActStatus))
      return status as AcceptanceActStatus;
    const parsed = Number(status);
    if (!Number.isNaN(parsed)) return acceptanceActStatusByCode[parsed];
  }
  return undefined;
};

// ---- SignalR (хаб sorting-center/shipment-manifest) ----

/** AcceptanceBagScanned: мешок принят в приёмку (отказы не рассылаются) */
export interface AcceptanceBagScannedHubPayload {
  acceptanceActId: number;
  bagNumber: string;
  status: AcceptanceBagStatus;
  userId: string;
  userLogin?: string | null;
  acceptanceDate: string;
}

/** AcceptanceActCreated / AcceptanceActClosed */
export interface AcceptanceActChangedHubPayload {
  acceptanceActId: number;
  number?: string | null;
  manifestNumber?: string | null;
  userId: string;
  userLogin?: string | null;
}

export const isSameUser = (a?: string | null, b?: string | null): boolean =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();
